import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Tag, Select, Modal } from "antd";
import { TableProps } from "antd";
import {
    CheckCircleOutlined,
    StopOutlined,
    PauseCircleOutlined,
    PoweroffOutlined,
    InfoCircleOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useLocation } from "react-router-dom";
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useMetaSetup } from '../context/MetaSetupContext';
import { toast } from "../common/Popover";
import Heading from "../common/Heading";
import coreAPIService from "../apiServices/coreAPIService";
import { CTW_API_ENDPOINT } from "../utils";
import '../assets/css/tablesShadow.css'

// ————————————————————————————————————————
// REAL API RESPONSE INTERFACES (from your cURL)
// ————————————————————————————————————————

interface MetaResponse {
    code: number;
    message: string;
    status: boolean;
}

interface Targeting {
    ageMSin?: number;
    ageMax?: number;
    genders?: number[];
    geoLocations?: {
        countries?: string[];
        cities?: Array<{ key: string; name: string }>;
    };
    interests?: Array<{ id: string; name: string }>;
}

interface DailyBudget {
    amount: string;
    currency: string;
}

interface AdsetData {
    id: string;
    name: string;
    status: string;
    effectiveStatus: string;
    dailyBudget?: string | DailyBudget;
    lifetimeBudget?: DailyBudget;
    bidAmount?: string;
    bidStrategy?: string;
    targeting?: Targeting;
    startTime?: string;
    endTime?: string;
    createdTime: string;
    updatedTime: string;
    promotedPageId?: string;
    whatsappNumber?: string | null;
    audience?: any;
    performanceGoal?: string | null;
    insights: {
        spend?: string;
        impressions?: string;
        clicks?: string;
        ctr?: string;
        cpc?: string;
        cpm?: string;
    }
}

interface ApiResponse {
    meta: MetaResponse;
    data: AdsetData[];
}

// ————————————————————————————————————————
// COMPONENT
// ————————————————————————————————————————

const skeletonBlockStyle = { height: "200px", width: "100%" };

const AdSetListing: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // campaign ID
    const { selectedAdAccount } = useMetaSetup();
    const location = useLocation();

    const [dataSource, setDataSource] = useState<AdsetData[]>([]);
    const [tableData, setTableData] = useState<AdsetData[]>([]);
    const [searchData, setSearchData] = useState<AdsetData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [value, setValue] = useState<string | null>(null);
    const [audienceModal, setAudienceModal] = useState<AdsetData | null>(null);

    // Breadcrumbs
    useEffect(() => {
        setBreadcrumbs([
            { title: <div className="text-lg cursor-pointer" onClick={() => navigate("/ctc-ads-meta/ad-accounts")}>Meta Ad Accounts</div> },
            { title: <div className="text-lg cursor-pointer" onClick={() => navigate(`/ctc-ads-meta/ad-accounts/adsets/${id}`, { state: { campaignName: location.state?.campaignName } })}>Meta Ad Sets</div> },
        ]);
    }, [setBreadcrumbs, navigate, id]);

    // Fetch Adsets — EXACT cURL replica
    const fetchAdsets = async () => {

        try {
            const response = await coreAPIService.get<ApiResponse>(CTW_API_ENDPOINT, `v2/meta-integration/campaigns/${id}/adsets`);

            if (!response.meta.status) {
                throw new Error(`HTTP ${response.meta.message}`);
            }

            if (response.meta.code === 200) {
                const adsets = response.data || [];
                setDataSource(adsets);
                setSearchData(adsets);
                setTableData(adsets);
            } else {
                toast("warning", response.meta.message || "Failed to fetch adsets");
            }
        } catch (err: any) {
            console.error("Fetch error:", err);
            toast("error", err.message || "Error fetching adsets");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchAdsets();
    }, [id]);

    const handleSwitchChange = (checked: boolean, record: AdsetData) => {
        // In real app, call API to enable/disable
        setDataSource(prev => prev.map(item =>
            item.id === record.id ? { ...item, status: checked ? "ACTIVE" : "PAUSED" } : item
        ));
    };

    const getStatusTag = (status: string) => {
        const map: Record<string, { color: string; icon: React.ReactNode }> = {
            "ACTIVE": { color: "green", icon: <CheckCircleOutlined /> },
            "PAUSED": { color: "purple", icon: <PauseCircleOutlined /> },
            "INACTIVE": { color: "red", icon: <StopOutlined /> },
            "DISABLED": { color: "gray", icon: <PoweroffOutlined /> },
        };
        const { color = "default", icon = <InfoCircleOutlined /> } = map[status] || {};
        return <Tag icon={icon} color={color}>{status}</Tag>;
    };

    const renderList = (items?: string[]) =>
        items?.length ? items.join(", ") : "—";

    const renderCityList = (cities?: any[]) =>
        cities?.length
            ? cities.map(c => c.name).join(", ")
            : "—";

    const truncate = (arr?: string[], limit = 3) =>
        arr && arr.length > limit
            ? `${arr.slice(0, limit).join(", ")} +${arr.length - limit} more`
            : arr?.join(", ") || "—";




    const columns: TableProps<AdsetData>['columns'] = [
        {
            title: "Name",
            key: "name",
            render: (_, record) => (
                <div>
                    <div className="font-medium">{record.name}</div>
                    <a className="text-blue-600 text-sm" onClick={() => navigate(`/ctc-ads-meta/ad-accounts/adset/ads/${record.id}`, { state: { campaignName: location.state?.campaignName, isTimesPanelCampaign: location.state?.isTimesPanelCampaign, whatsAppNumber: record.whatsappNumber } })}>
                        View Ads →
                    </a>
                </div>
            ),
            fixed: 'left',
            width: 250,
        },
        {
            title: "Status",
            dataIndex: "effectiveStatus",
            render: (_, record) => getStatusTag(record.effectiveStatus),
            align: "center",
            width: 120,
        },
        {
            title: "Daily Budget / Lifetime Budget",
            key: "budget",
            render: (_, record) => {
                const daily = typeof record.dailyBudget === 'string' ? Number(record.dailyBudget) : Number(record.dailyBudget?.amount);
                const lifetime = typeof record.lifetimeBudget === 'string' ? Number(record.lifetimeBudget) : Number(record.lifetimeBudget?.amount);
                return `₹${daily == 0 ? "-" : daily / 100} / ₹${lifetime == 0 ? "-" : lifetime / 100}`;
            },
            align: "center",
            width: 200,
        },
        {
            title: "FB Page",
            dataIndex: "promotedPageName",
            render: (val) => val || "-",
            align: "center",
            width: 150,
        },
        {
            title: "WhatsApp Number",
            dataIndex: "whatsappNumber",
            render: (val) => val || "-",
            align: "center",
            width: 150,
        },
        {
            title: "Targeting",
            key: "audience",
            render: (_, record) => (
                <div className="truncate max-w-xs"><EyeOutlined
                    className="cursor-pointer text-blue-600"
                    onClick={() => setAudienceModal(record)}
                />
                </div>
            ),
            width: 100,
            align: "center",
        },
        {
            title: "Spend",
            dataIndex: "insight.spend",
            render: (_, record) => record.insights.spend ? `₹${record.insights.spend}` : "₹0",
            align: "center",
            width: 120,
        },
        {
            title: "Performance Goal",
            dataIndex: "performanceGoal",
            render: (val) => val || "-",
            align: "center",
            width: 150,
        },
    ];

    const handleSearch = (val: string) => {
        const filtered = dataSource.filter(d =>
            d.name.toLowerCase().includes(val.toLowerCase())
        );
        setSearchData(filtered);
    };

    const handleChange = (val: string) => {
        if (!val) {
            setValue(null);
            handleStatusChange("ALL");
            return;
        }
        const filtered = dataSource.filter(d => d.id === val);
        setTableData(filtered);
        setValue(val);
    };

    const handleStatusChange = (status: string) => {
        setValue(null);
        if (status === "ALL") {
            setTableData(dataSource);
        } else {
            setTableData(dataSource.filter(d => d.status === status));
        }
    };

    return (
        <section className="px-10 py-2">
            <div className="bg-white shadow-md rounded-lg p-6">
                <Heading title="Ad Sets" />

                {/* Account Info */}
                <div className="flex justify-between items-start mb-6">
                    <div className="grid grid-cols-2 gap-x-8 text-sm">
                        <p><strong>Meta Ad Account:</strong> {selectedAdAccount?.name || "-"}</p>
                        <p><strong>Campaign Name:</strong> {location.state?.campaignName || "-"}</p>
                    </div>

                    <div className="flex gap-3">
                        <Select
                            showSearch
                            placeholder="Search ad set..."
                            className="w-80"
                            value={value}
                            onSearch={handleSearch}
                            onChange={handleChange}
                            filterOption={false}
                            options={searchData.map(d => ({ value: d.id, label: d.name }))}
                            allowClear={true}
                        />
                        <Select
                            placeholder="Status"
                            style={{ width: 160 }}
                            onChange={handleStatusChange}
                        >
                            <Select.Option value="ALL">All Status</Select.Option>
                            <Select.Option value="ACTIVE">Active</Select.Option>
                            <Select.Option value="PAUSED">Paused</Select.Option>
                            <Select.Option value="INACTIVE">Inactive</Select.Option>
                        </Select>
                    </div>
                </div>

                {/* Table */}
                <div className="p-4 bg-gray-50">
                    {loading ? (
                        <Table
                            columns={columns}
                            dataSource={[]}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                            loading={true}
                        />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                        />
                    )}
                </div>
            </div>
            <Modal
                open={audienceModal && audienceModal?.audience}
                onCancel={() => setAudienceModal(null)}
                footer={null}
                width={700}
                title="Audience Targeting"
            >
                {audienceModal?.audience && (
                    <div className="space-y-4">

                        {/* Custom Audiences */}
                        <section>
                            <strong>Audiences</strong>
                            <div>
                                {audienceModal.audience.customAudiences?.length
                                    ? audienceModal.audience.customAudiences
                                        .map((a: any) => a.name)
                                        .join(", ")
                                    : "None"}
                            </div>
                        </section>

                        {/* Age */}
                        <section>
                            <strong>Age</strong>
                            <div>
                                {audienceModal.audience.ageMin} – {audienceModal.audience.ageMax}
                            </div>
                        </section>


                        {/* Included Locations */}
                        <section>
                            <strong>Included Locations</strong>
                            <div>
                                Cities: {renderCityList(audienceModal.audience.geoLocations?.cities)}
                            </div>
                            <div>
                                Location Types:{" "}
                                {renderList(audienceModal.audience.geoLocations?.locationTypes)}
                            </div>
                        </section>

                        {/* Excluded Locations */}
                        <section>
                            <strong className="text-red-600">Excluded Locations</strong>
                            <div>
                                Cities:{" "}
                                {renderCityList(
                                    audienceModal.audience.excludedGeoLocations?.cities
                                )}
                            </div>
                            <div>
                                Regions:{" "}
                                {renderList(
                                    audienceModal.audience.excludedGeoLocations?.regions?.map(
                                        (r: any) => r.name
                                    )
                                )}
                            </div>
                            <div>
                                Places:{" "}
                                {renderList(
                                    audienceModal.audience.excludedGeoLocations?.places?.map(
                                        (p: any) => p.name
                                    )
                                )}
                            </div>
                        </section>

                        {/* Platforms */}
                        <section>
                            <strong>Platforms</strong>
                            <div>{renderList(audienceModal.audience.publisherPlatforms)}</div>
                        </section>

                        {/* Placements */}
                        <section>
                            <strong>Placements</strong>
                            <div>
                                Facebook:{" "}
                                {renderList(audienceModal.audience.facebookPositions)}
                            </div>
                            <div>
                                Instagram:{" "}
                                {renderList(audienceModal.audience.instagramPositions)}
                            </div>
                        </section>

                        {/* Devices */}
                        <section>
                            <strong>Devices</strong>
                            <div>{renderList(audienceModal.audience.devicePlatforms)}</div>
                        </section>
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default AdSetListing;